/**
 * File: Checkbox.js
 * Origin: isotect
 * Description: Checkbox component for forms.
 *
 * Version: v3.0.2
 * Created: 2025-07-24
 * Last Updated: 2026-05-01
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

/**
 * Checkbox component renders a checkbox input with an associated label.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {string} props.id - Unique identifier for the checkbox
 * @param {string} props.label - Text displayed next to the checkbox
 * @param {boolean} props.checked - Controls whether the checkbox is checked
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} props.onChange - Callback fired when the checkbox value changes
 * @param {boolean} [props.isRequired] - Whether the checkbox is marked as required
 *
 * @returns {JSX.Element} A checkbox input element with a label
 *
 * @example
 * <Checkbox
 *   id="terms"
 *   label="I agree to the terms and conditions"
 *   checked={isChecked}
 *   onChange={handleChange}
 *   isRequired
 * />
 */
function Checkbox({ id, label, checked, onChange, isRequired = false }) {
    const handleChange = (e) => {
        onChange(e.target.checked);
    };

    return (
        <>
            <div className={`checkbox ${!label ? "checkbox--no-label" : ""}`}>
                <input
                    className="checkbox"
                    type="checkbox"
                    id={id}
                    checked={checked}
                    onChange={handleChange}
                    required={isRequired}
                />
                {label && <label htmlFor={id}>{label}</label>}
            </div>
        </>
    );
}

export default Checkbox;
